// src/api/axiosClient.js
import axios from "axios";
import { notifySessionExpired } from "../auth/sessionExpired";
import { refreshAccessToken } from "../utils/auth";

/**
 * axiosClient
 * Configuración de Axios para la comunicación con el API Gateway.
 * Incluye interceptores para manejo de errores y se podrá extender con
 * encabezados de autenticación, tokens, etc.
 */
const axiosClient = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL,
  timeout: 20000,
});

axiosClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token"); // Obtén el token del almacenamiento local
    // console log de la ruta
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Interceptor de respuesta
axiosClient.interceptors.response.use(
  (response) => {
    // Si la respuesta ya tiene la estructura esperada, devolverla tal como está
    if (response.data && typeof response.data === "object") {
      return response.data;
    }
    // Si no, devolver la respuesta completa
    return response;
  },
  async (error) => {
    const original = error?.config;
    const status = error?.response?.status;
    const isRefreshCall = original?.url?.includes("/auth/refresh");

    if (status === 401 && original && !original._retry && !isRefreshCall && localStorage.getItem("refresh_token")) {
      original._retry = true;
      try {
        const failedToken =
          (original.headers && (original.headers.Authorization || original.headers.authorization)
            ? String(original.headers.Authorization || original.headers.authorization).replace(/^Bearer\s+/i, '')
            : null) || localStorage.getItem("token");
        const newToken = await refreshAccessToken({
          backendUrl: axiosClient.defaults.baseURL,
          failedToken,
        });
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${newToken}`;
        return axiosClient(original);
      } catch (e) {
        notifySessionExpired();
        return Promise.reject(error);
      }
    }

    if (!axios.isCancel(error)) {
      console.error("Axios error:", error);
    }
    // Sesión zombi: si el refresco falló, `refreshAccessToken` ya borró los tokens,
    // así que las siguientes llamadas salen SIN cabecera y el backend responde 401
    // "Authorization header missing". Condicionar el aviso a que haya token dejaba
    // al usuario dando vueltas con alerts crípticos en vez de mandarlo al login.
    // Se excluyen los endpoints de autenticación (un login con contraseña mala
    // devuelve 401 y no es una sesión caducada).
    const isAuthEndpoint = /\/(login|auth\/|signup|reset|verify)/i.test(
      String(original?.url || "")
    );
    if (status === 401 && !isAuthEndpoint) {
      notifySessionExpired();
    }
    return Promise.reject(error);
  }
);

export default axiosClient;
